import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FaTimesCircle, FaTrash } from 'react-icons/fa';

import { Provider } from '../../Chakra';
import useCurrentUser from '../../Hooks/userCurrentUser';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import { EVENT_TITLE } from '../../Constants/AppConstants';

import { AddSchoolModal } from './AddSchoolModal';
import { AddStudentModal } from './AddStudentModal';
import { DeleteCoalitionModal } from './DeleteCoalitionModal';


const COALITION_FIELDS = `
  id
  status
  tenantId
  code
  fetchEventCompetition {
    id
    title
  }
  fetchCoalitionsSchools{
    id
    status
    schoolId
    tenantId
    coalitionId
    fetchSchool {
      id
      name
    }
    fetchCoalitionMembers {
      id
      user {
        id
        code
        firstname
        lastname
      }
    }
  }
`;

const FETCH_COALITION_DETAIL = gql`
query fetchCoalitionDetail($id: Int!){
  fetchCoalitionDetail(id: $id ) {
    ${COALITION_FIELDS}
  }
}
`;

const REMOVE_SCHOOL_FROM_COALITION = gql`
mutation removeSchoolFromCoalition($id: ID!, $schoolId: Int!) {
  removeSchoolFromCoalition(input: {
    id: $id,
    schoolId: $schoolId,
  }) {
    coalition {
      ${COALITION_FIELDS}
    }
  }
}
`;

const REMOVE_USER_FROM_COALITION = gql`
mutation removeUserFromCoalitionSubteam($coalitionId: ID!, $userId: ID!) {
  removeUserFromCoalitionSubteam(input: {
    coalitionId: $coalitionId,
    userId: $userId,
  }) {
    coalitionMembers {
      id
      user {
        id
        code
        firstname
        lastname
      }
    }
  }
}
`;

const FETCH_EVENT_HEADER = gql`
  query fetchEventDetail($id: Int!) {
    fetchEventDetail(id: $id) {
      id
      title
    }
  }
`;

const CoalitionsEdit = () => {
  const toast = useToast();
  const { currentUser, loading: userLoading } = useCurrentUser(true);
  const { id, coalitionId } = useParams();
  const {
    isOpen: schoolModalIsOpen,
    onOpen: schoolModalOnOpen,
    onClose: schoolModalOnClose,
  } = useDisclosure();
  const {
    isOpen: studentModalIsOpen,
    onOpen: studentModalOnOpen,
    onClose: studentModalOnClose,
  } = useDisclosure();
  const {
    isOpen: deleteCoalitionModalIsOpen,
    onOpen: deleteCoalitionModalOnOpen,
    onClose: deleteCoalitionModalOnClose,
  } = useDisclosure();

  const { data: eventData, loading: eventLoading } = useQuery(FETCH_EVENT_HEADER, {
    skip: !currentUser,
    variables: {
      id,
    },
  });
  const event = eventData?.fetchEventDetail ?? {};
  useHeaderComponentsMutation({
    title: 'EDIT COALITION',
    backLink: `/event-manage/${id}/coalitions`,
    components: [{ key: EVENT_TITLE, value: event.title }],
  });

  const { data, loading: coalitionLoading } = useQuery(
    FETCH_COALITION_DETAIL, {
      variables: { id: parseInt(coalitionId, 10) },
      skip: !currentUser,
      onError: (err) => {
        toast({
          title: err.message,
          status: 'error',
          isClosable: true,
          position: 'top',
        });
      },
    },
  );
  const coalition = data?.fetchCoalitionDetail ?? {};

  const [removeSchoolFromCoalition] = useMutation(REMOVE_SCHOOL_FROM_COALITION);
  const [schoolDeleting, setSchoolDeleting] = useState([]);

  const [removeUserFromCoalition] = useMutation(REMOVE_USER_FROM_COALITION, {
    refetchQueries: ['fetchCoalitionDetail'],
  });
  const [userDeleting, setUserDeleting] = useState([]);

  const loading = userLoading || eventLoading || coalitionLoading;
  if (loading) {
    return (
      <Spinner />
    );
  }

  const onDelete = (schoolId) => {
    setSchoolDeleting([...schoolDeleting, schoolId]);
    removeSchoolFromCoalition({
      variables: {
        id: coalitionId,
        schoolId,
      },
    })
      .catch((err) => {
        toast({
          title: err.message,
          status: 'error',
          isClosable: true,
          position: 'top',
        });
      })
      .finally(() => {
        setSchoolDeleting(schoolDeleting.filter((sid) => sid !== schoolId));
      });
  };

  const onDeleteStudent = (userId) => {
    setUserDeleting([...userDeleting, userId]);
    removeUserFromCoalition({
      variables: {
        coalitionId,
        userId,
      },
    })
      .catch((err) => {
        toast({
          title: err.message,
          status: 'error',
          isClosable: true,
          position: 'top',
        });
      })
      .finally(() => {
        setUserDeleting(userDeleting.filter((sid) => sid !== userId));
      });
  };

  return (
    <Flex direction="column" width="100%" marginBlock={4} gap={4}>
      {schoolModalIsOpen && (
        <AddSchoolModal
          isOpen={schoolModalIsOpen}
          onOpen={schoolModalOnOpen}
          onClose={schoolModalOnClose}
          eventId={event.id}
          coalitionId={coalition.id}
          schoolIdsInCoalition={coalition.fetchCoalitionsSchools.map((cs) => cs.schoolId)}
        />
      )}
      {studentModalIsOpen && (
        <AddStudentModal
          isOpen={studentModalIsOpen}
          onOpen={studentModalOnOpen}
          onClose={studentModalOnClose}
          eventId={event.id}
          coalitionId={coalition.id}
        />
      )}
      {deleteCoalitionModalIsOpen && (
        <DeleteCoalitionModal
          isOpen={deleteCoalitionModalIsOpen}
          onClose={deleteCoalitionModalOnClose}
        />
      )}
      <Box>
        <Heading size="lg">
          Coalition
          {' '}
          {coalition.code}
        </Heading>
        <Text>
          Competition:
          {' '}
          {coalition.fetchEventCompetition.title}
        </Text>
      </Box>

      {coalition.fetchCoalitionsSchools.map((cs) => {
        const isBeingDeleted = schoolDeleting.includes(cs.fetchSchool.id);
        return (
          <Flex
            key={cs.id}
            alignItems="center"
            padding={3}
            borderColor="gray.200"
            borderWidth={1}
            borderRadius={4}
            gap={2}
          >
            <Flex direction="column" flexGrow={1}>
              <Heading as="h3" size="md">{cs.fetchSchool.name}</Heading>
              {cs.fetchCoalitionMembers.map((cm) => (
                <Flex key={cm.id}>
                  <IconButton
                    icon={<FaTimesCircle />}
                    variant="ghost"
                    onClick={() => onDeleteStudent(cm.user.id)}
                    colorScheme="red"
                    size="xs"
                    isLoading={userDeleting.includes(cm.user.id)}
                  />
                  <Flex direction="column" flexGrow={1}>
                    {cm.user.code}
                    {' '}
                    {cm.user.firstname}
                    {' '}
                    {cm.user.lastname}
                  </Flex>
                </Flex>
              ))}
            </Flex>
            <IconButton
              icon={<FaTrash />}
              variant="ghost"
              onClick={() => onDelete(cs.fetchSchool.id)}
              colorScheme="red"
              size="xs"
              isLoading={isBeingDeleted}
            />
          </Flex>
        );
      })}

      <Flex gap={4} width="100%">
        <Button
          variant="outline"
          flexGrow={1}
          onClick={schoolModalOnOpen}
        >
          Add New Schools
        </Button>
        <Button
          variant="outline"
          flexGrow={1}
          onClick={studentModalOnOpen}
        >
          Add New Student
        </Button>
        <Button
          variant="outline"
          colorScheme="red"
          flexGrow={1}
          onClick={deleteCoalitionModalOnOpen}
        >
          Delete Coalition
        </Button>
      </Flex>
    </Flex>
  );
};

export const CoalitionsEditPage = () => (
  <Provider>
    <Flex direction="column" height="100%" width="100%" justifyContent="center" alignContent="center">
      <CoalitionsEdit />
    </Flex>
  </Provider>
);
